import { http } from "./http.js";
import type {
  DeviceListResponseDto,
  DeviceStatusResponseDto,
  DeviceControlResponseDto,
  UnclaimedDeviceListResponseDto,
  ScanDevicesResponseDto,
} from "@laxmi-ai/shared";
import type {
  StartAutomationResponseDto,
  AutomationStatusDto,
} from "@laxmi-ai/shared";
import type {
  ContentUploadResponseDto,
  ContentPushResponseDto,
} from "@laxmi-ai/shared";
import type {
  StreamingConfigPayload,
  StreamingStreamsResponse,
} from "@laxmi-ai/shared";
import type {
  Platform,
  SocialAccount,
  SocialConnectSession,
  SocialAuditEvent,
  SocialAuditEventType,
} from "@laxmi-ai/shared";
import type {
  CreatorInvitationListItem,
  CreateCreatorInvitationRequest,
  CreateCreatorInvitationResponse,
} from "@laxmi-ai/shared";

export type { Platform, SocialAccount, SocialConnectSession, SocialAuditEvent, SocialAuditEventType };

export async function fetchDevices(): Promise<DeviceListResponseDto> {
  const { data } = await http.get<DeviceListResponseDto>("/devices");
  return data;
}

// Streaming pipeline negotiation + diagnostics
export async function getStreamingConfig(): Promise<StreamingConfigPayload> {
  const { data } = await http.get<StreamingConfigPayload>("/v1/streaming/config");
  return data;
}

/**
 * The agency view: its own devices' streams and sockets. Server process stats
 * (`node`) are platform data and come back null here.
 */
export type OrgStreamingStreamsResponse = Omit<StreamingStreamsResponse, "node"> & {
  node: StreamingStreamsResponse["node"] | null;
};

export async function getStreamingStreams(): Promise<OrgStreamingStreamsResponse> {
  const { data } = await http.get<OrgStreamingStreamsResponse>(
    "/v1/admin/streaming/streams",
  );
  return data;
}

export interface AgentDto {
  id: string;
  orgId: string;
  name: string;
  lastSeen: string | null;
  createdAt: string;
  isConnected: boolean;
}

export async function listAgents(): Promise<{ agents: AgentDto[] }> {
  const { data } = await http.get<{ agents: AgentDto[] }>("/v1/agents");
  return data;
}

export async function createAgent(
  name: string,
): Promise<{ agent: AgentDto; token: string; message: string }> {
  const { data } = await http.post<{
    agent: AgentDto;
    token: string;
    message: string;
  }>("/v1/agents", { name });
  return data;
}

export async function deleteAgent(
  agentId: string,
): Promise<{ status: string }> {
  const { data } = await http.delete<{ status: string }>(
    `/v1/agents/${encodeURIComponent(agentId)}`,
  );
  return data;
}

export interface WirelessPairResult {
  success: boolean;
  /** connected: usable now; paired: needs the connect port; failed. */
  status: "connected" | "paired" | "failed";
  message?: string;
  serial?: string;
}

/**
 * Pair and/or connect a phone over Wi-Fi through a Connector. Resolves with
 * the Connector's real outcome (the request waits for it, up to ~50 s).
 */
export async function pairAgentDevice(params: {
  agentId: string;
  ip: string;
  pairPort?: string;
  code?: string;
  connectPort?: string;
}): Promise<WirelessPairResult> {
  const { data } = await http.post<WirelessPairResult>(
    `/v1/agents/${encodeURIComponent(params.agentId)}/pair`,
    {
      ip: params.ip,
      ...(params.pairPort !== undefined ? { pairPort: params.pairPort } : {}),
      ...(params.code !== undefined ? { code: params.code } : {}),
      ...(params.connectPort !== undefined ? { connectPort: params.connectPort } : {}),
    },
    { timeout: 60_000 },
  );
  return data;
}

export async function createAgentPairingCode(): Promise<{ code: string; expiresAt: string }> {
  const { data } = await http.post<{ code: string; expiresAt: string }>("/v1/agents/pairing-code");
  return data;
}

export async function fetchDeviceStatus(
  serial: string,
): Promise<DeviceStatusResponseDto> {
  const { data } = await http.get<DeviceStatusResponseDto>(
    `/devices/${encodeURIComponent(serial)}/status`,
  );
  return data;
}

export async function fetchUnclaimedDevices(): Promise<UnclaimedDeviceListResponseDto> {
  const { data } = await http.get<UnclaimedDeviceListResponseDto>(
    "/devices/unclaimed",
  );
  return data;
}

export async function scanDevices(): Promise<ScanDevicesResponseDto> {
  const { data } = await http.post<ScanDevicesResponseDto>("/devices/scan");
  return data;
}

export async function claimDevice(
  serial: string,
): Promise<{ success: boolean; message: string }> {
  const { data } = await http.post<{ success: boolean; message: string }>(
    `/devices/${encodeURIComponent(serial)}/claim`,
  );
  return data;
}

export async function removeDevice(
  serial: string,
): Promise<{ success: boolean; message: string }> {
  const { data } = await http.delete<{ success: boolean; message: string }>(
    `/devices/${encodeURIComponent(serial)}`,
  );
  return data;
}

export async function controlDevice(
  serial: string,
  action: string,
  params: Record<string, unknown> = {},
): Promise<DeviceControlResponseDto> {
  const { data } = await http.post<DeviceControlResponseDto>(
    `/devices/${encodeURIComponent(serial)}/control`,
    { action, params },
  );
  return data;
}

export async function uploadContent(
  file: File,
): Promise<ContentUploadResponseDto> {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await http.post<ContentUploadResponseDto>(
    "/content/upload",
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 600000,
    },
  );
  return data;
}

export async function listContentFiles(): Promise<{
  files: Array<{
    id: string;
    originalName: string;
    mimeType: string;
    sizeBytes: number;
    uploadedAt: string;
  }>;
  count: number;
}> {
  const { data } = await http.get("/content/files");
  return data;
}

export async function pushContentToDevice(
  fileId: string,
  deviceSerial: string,
): Promise<ContentPushResponseDto> {
  const { data } = await http.post<ContentPushResponseDto>(
    `/content/push/${encodeURIComponent(fileId)}`,
    { deviceSerial },
  );
  return data;
}

export async function startAutomation(params: {
  deviceSerial: string;
  taskType: string;
  accountId?: string;
  caption?: string;
  hashtags?: string[];
  mediaPath?: string;
}): Promise<StartAutomationResponseDto> {
  const { data } = await http.post<StartAutomationResponseDto>(
    "/automation/start",
    params,
  );
  return data;
}

export async function stopAutomation(
  taskId: string,
): Promise<{ success: boolean }> {
  const { data } = await http.post<{ success: boolean }>("/automation/stop", {
    taskId,
  });
  return data;
}

export async function retryFailedTasks(): Promise<{ success: boolean; retried: number }> {
  const { data } = await http.post<{ success: boolean; retried: number }>("/automation/retry-failed");
  return data;
}

export async function stopAllTasks(): Promise<{ success: boolean; stopped: number }> {
  const { data } = await http.post<{ success: boolean; stopped: number }>("/automation/stop-all");
  return data;
}

export async function getAutomationStatus(
  taskId: string,
): Promise<AutomationStatusDto> {
  const { data } = await http.get<AutomationStatusDto>(
    `/automation/status/${encodeURIComponent(taskId)}`,
  );
  return data;
}

export async function uploadAndPost(
  deviceSerial: string,
  file: File,
  caption?: string,
  hashtags?: string[],
  accountId?: string,
  onUploadProgress?: (progressEvent: any) => void,
): Promise<{ success: boolean; taskId: string }> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("deviceSerial", deviceSerial);
  if (caption !== undefined) formData.append("caption", caption);
  if (hashtags !== undefined)
    formData.append("hashtags", JSON.stringify(hashtags));
  if (accountId !== undefined) formData.append("accountId", accountId);

  const { data } = await http.post<{ success: boolean; taskId: string }>(
    "/automation/upload-and-post",
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress,
      timeout: 600000,
    },
  );
  return data;
}

export async function createAssignment(params: {
  fileId: string;
  accountId: string;
  deviceSerial: string;
  caption?: string;
  hashtags?: string[];
  scheduledAt?: string;
}): Promise<{ success: boolean; assignment: unknown }> {
  const { data } = await http.post("/assignments", params);
  return data;
}

export async function listAssignments(): Promise<{
  assignments: unknown[];
  count: number;
}> {
  const { data } = await http.get("/assignments");
  return data;
}

export async function createVariation(
  fileId: string,
  config?: unknown,
): Promise<{ success: boolean; variation: unknown }> {
  const { data } = await http.post("/variations", { fileId, config });
  return data;
}

export async function getFleetAnalytics(): Promise<{
  success: boolean;
  analytics: unknown;
}> {
  const { data } = await http.get("/analytics/fleet");
  return data;
}

export async function getAccountMetrics(
  accountId: string,
): Promise<{ success: boolean; metrics: unknown }> {
  const { data } = await http.get(
    `/analytics/account/${encodeURIComponent(accountId)}`,
  );
  return data;
}

export async function getPostingHistory(limit?: number): Promise<{
  success: boolean;
  data: Array<{
    id: string;
    account_id: string;
    file_id: string;
    platform: string;
    status: string;
    posted_at: string;
    duration_ms: number;
    caption: string | null;
    error: string | null;
    device_serial: string;
  }>;
}> {
  const { data } = await http.get("/v1/integrity/history", {
    params: { limit },
  });
  return data;
}

export async function getPostingHistoryByAccount(
  accountId: string,
  limit?: number,
): Promise<{
  success: boolean;
  data: Array<{
    id: string;
    account_id: string;
    file_id: string;
    platform: string;
    status: string;
    posted_at: string;
    duration_ms: number;
    caption: string | null;
    error: string | null;
    device_serial: string;
  }>;
}> {
  const { data } = await http.get(
    `/v1/integrity/history/${encodeURIComponent(accountId)}`,
    { params: { limit } },
  );
  return data;
}

export async function getContentHash(fileId: string): Promise<{
  success: boolean;
  data: { sha256: string; file_size: number; mime_type: string } | null;
}> {
  const { data } = await http.get(
    `/v1/integrity/hash/${encodeURIComponent(fileId)}`,
  );
  return data;
}

/** An account's warmup as the backend's pre-flight enforces it. */
export interface AccountWarmup {
  startedAt: string;
  endsAt: string;
  /** Posts allowed today; rises by one a day up to maxDailyLimit. */
  dailyLimit: number;
  maxDailyLimit: number;
  /** Posts recorded today (UTC day). */
  postsToday: number;
  /** Still limiting posts. */
  active: boolean;
}

export async function getAccountHealth(accountId: string): Promise<{
  success: boolean;
  /** Absent from servers older than 2026-10-19. */
  warmup?: AccountWarmup | null;
  data: {
    health_score: number;
    total_posts: number;
    successful_posts: number;
    failed_posts: number;
    consecutive_failures: number;
    is_paused: boolean;
    pause_reason: string | null;
    last_post_at: string | null;
  } | null;
}> {
  const { data } = await http.get(
    `/v1/anti-flagging/health/${encodeURIComponent(accountId)}`,
  );
  return data;
}

export async function initWarmup(
  accountId: string,
  warmupDays?: number,
): Promise<{ success: boolean; message: string; data?: { started: boolean } }> {
  const { data } = await http.post("/v1/anti-flagging/warmup", {
    accountId,
    warmupDays,
  });
  return data;
}

export async function endWarmup(
  accountId: string,
): Promise<{ success: boolean; data: { ended: boolean } }> {
  const { data } = await http.post("/v1/anti-flagging/warmup/end", { accountId });
  return data;
}

export async function resumeAccount(
  accountId: string,
): Promise<{ success: boolean; message: string; data?: { wasPaused: boolean } }> {
  const { data } = await http.post("/v1/anti-flagging/resume", { accountId });
  return data;
}

// Dashboard
export async function getDashboardStats(): Promise<{
  success: boolean;
  totalCreators: number;
  totalDevices: number;
  onlineDevices: number;
  postsToday: number;
  failedPosts: number;
  duplicatesPrevented: number;
  storageUsedPercent: number;
  storageUsed: string;
  storageTotal: string;
}> {
  const { data } = await http.get("/v1/dashboard/stats");
  return data;
}

// Dashboard "Get started" checklist: which setup steps the agency has done
// (backend services/onboarding.service.ts; computed, never stored).
export type GetStartedStep = "connector" | "phone" | "creator" | "creatorPhone" | "account" | "media" | "post";
export async function getOnboarding(): Promise<{
  success: boolean;
  steps: Record<GetStartedStep, boolean>;
  firstPostLive: boolean;
}> {
  const { data } = await http.get("/v1/onboarding");
  return data;
}

// Activity: the agency's audit trail in plain language (backend
// services/activity-feed.service.ts). Each kind needs the permission that
// covers that work; creators see only their own.
export type ActivityCategory = "posts" | "media" | "phones" | "accounts" | "creators" | "agency";
export interface ActivityItem {
  id: string;
  at: string;
  category: ActivityCategory;
  tone: "success" | "error" | "warning" | "info";
  text: string;
  detail: string | null;
  link: string | null;
}

export async function listActivity(params?: {
  limit?: number;
  category?: ActivityCategory;
  before?: string;
}): Promise<{
  success: boolean;
  items: ActivityItem[];
  /** Pass as `before` for the next (older) page; null on the last page. */
  nextBefore: string | null;
  /** The kinds of activity this user may see. */
  categories: ActivityCategory[];
}> {
  const { data } = await http.get("/v1/activity", { params });
  return data;
}

// Legacy activity_logs table (only the unrouted Notifications page reads it)
export async function getActivityLogs(params?: {
  severity?: string;
  limit?: number;
  offset?: number;
}): Promise<{
  success: boolean;
  entries: Array<{
    id: string;
    orgId: string;
    action: string;
    entity: string;
    severity: string;
    userId: string | null;
    createdAt: string;
  }>;
  total: number;
}> {
  const { data } = await http.get("/v1/activity-logs", { params });
  return data;
}

// System Status
export async function getSystemStatus(): Promise<{
  success: boolean;
  cpu: { label: string; value: string; progress: number; color: string };
  ram: { label: string; value: string; progress: number; color: string };
  disk: { label: string; value: string; progress: number; color: string };
  network: { label: string; value: string; progress: number; color: string };
  services: Array<{
    name: string;
    status: string;
    detail: string;
    uptime: string;
  }>;
  uptime: string;
  nodeVersion: string;
  platform: string;
  operations: {
    devices: { total: number; connected: number; offline: number; unauthorized: number; unknown: number };
    agents: { connected: number };
    worker: { publishing: { started: boolean; processing: boolean; inFlight: number } | null };
    jobs: {
      queued: number;
      running: number;
      retrying: number;
      completed: number;
      failed: number;
      cancelled: number;
      stuck: number;
      unconfirmed?: number;
      blocked?: number;
    };
    recentFailures: Array<{
      id: string;
      failureType: string | null;
      errorMessage: string | null;
      updatedAt: string | null;
    }>;
  };
}> {
  const { data } = await http.get("/v1/system/status");
  return data;
}

// Creators
export async function listCreatorProfiles(params?: {
  platform?: string;
}): Promise<{
  success: boolean;
  profiles: Array<{
    id: string;
    orgId: string;
    name: string;
    platform: string;
    platformUsername: string;
    profileData: Record<string, unknown>;
    status: string;
    createdAt: string;
    updatedAt: string;
  }>;
  count: number;
}> {
  const { data } = await http.get("/v1/creators/profiles", { params });
  return data;
}

export async function getCreatorProfile(id: string): Promise<{
  success: boolean;
  profile: {
    id: string;
    orgId: string;
    name: string;
    platform: string;
    platformUsername: string;
    profileData: Record<string, unknown>;
    status: string;
    createdAt: string;
    updatedAt: string;
    deviceSerial: string | null;
  };
  accounts: Array<{
    id: string;
    platform: string;
    username: string;
    status: string;
    riskLevel: string;
    linkedDeviceSerial: string | null;
    proxyHost: string | null;
    createdAt: string;
  }>;
}> {
  const { data } = await http.get(
    `/v1/creators/profiles/${encodeURIComponent(id)}`,
  );
  return data;
}

export async function createCreatorProfile(params: {
  name: string;
  platform: string;
  platformUsername: string;
  profileData?: Record<string, unknown>;
  deviceSerial?: string | null;
}): Promise<{ success: boolean; profile: unknown }> {
  const { data } = await http.post("/v1/creators/profiles", params);
  return data;
}

// Calendar
export async function listCalendarEntries(params?: {
  status?: string;
  platform?: string;
  from?: string;
  to?: string;
  creatorProfileId?: string;
}): Promise<{
  success: boolean;
  entries: Array<{
    id: string;
    title: string;
    platform: string;
    scheduledAt: string;
    status: string;
    contentType: string;
    creatorProfileId: string | null;
    caption: string | null;
    hashtags: string[] | null;
  }>;
  count: number;
}> {
  const { data } = await http.get("/v1/creators/calendar", { params });
  return data;
}

// Create a new calendar (schedule) entry for a creator
export async function createCalendarEntry(params: {
  creatorProfileId: string;
  title: string;
  platform: string;
  scheduledAt: string;
  description?: string;
  contentType?: string;
  fileId?: string;
  caption?: string;
  hashtags?: string[];
}): Promise<{
  success: boolean;
  entry: {
    id: string;
    title: string;
    platform: string;
    scheduledAt: string;
    status: string;
    contentType: string;
    creatorProfileId: string | null;
    caption: string | null;
    hashtags: string[] | null;
  };
}> {
  const { data } = await http.post("/v1/creators/calendar", params);
  return data;
}

// Delete a calendar (schedule) entry
export async function deleteCalendarEntry(
  id: string,
): Promise<{ success: boolean }> {
  const { data } = await http.delete(
    `/v1/creators/calendar/${encodeURIComponent(id)}`,
  );
  return data;
}


// Update Creator Profile (device assignment)
export async function getAccountHealthList(
  accountIds: string[],
): Promise<Record<string, Record<string, unknown> | null>> {
  const result: Record<string, Record<string, unknown> | null> = {};
  for (const id of accountIds) {
    try {
      const { data } = await http.get(
        `/v1/anti-flagging/health/${encodeURIComponent(id)}`,
      );
      result[id] = data.data;
    } catch {
      result[id] = null;
    }
  }
  return result;
}

// Update Creator Profile (device assignment)
export async function updateCreatorProfile(
  id: string,
  updates: {
    name?: string;
    platform?: string;
    platformUsername?: string;
    status?: string;
    deviceSerial?: string | null;
  },
): Promise<{ success: boolean; profile: unknown }> {
  const { data } = await http.patch(
    `/v1/creators/profiles/${encodeURIComponent(id)}`,
    updates,
  );
  return data;
}

export async function deleteCreatorProfile(
  id: string,
): Promise<{ success: boolean }> {
  const { data } = await http.delete<{ success: boolean }>(
    `/v1/creators/profiles/${encodeURIComponent(id)}`,
  );
  return data;
}

// Trigger an immediate sync of the creator's content to the device.
export async function syncCreatorNow(
  id: string,
): Promise<{ status: string; enqueued: number }> {
  const { data } = await http.post<{ status: string; enqueued: number }>(
    `/v1/creators/${encodeURIComponent(id)}/sync`,
  );
  return data;
}

// Social Accounts (device-native login sessions)
export interface ConnectSessionDevice {
  serial: string;
  model: string;
  status: string;
  remoteAvailable: boolean;
}

export async function listCreatorAccounts(
  creatorId: string,
): Promise<{ success: boolean; accounts: SocialAccount[]; count: number }> {
  const { data } = await http.get<{
    success: boolean;
    accounts: SocialAccount[];
    count: number;
  }>(`/v1/creators/${encodeURIComponent(creatorId)}/accounts`);
  return data;
}

export async function connectSocialAccount(params: {
  creatorId: string;
  platform: Platform;
}): Promise<{
  success: boolean;
  session: SocialConnectSession;
  device: ConnectSessionDevice;
}> {
  const { data } = await http.post<{
    success: boolean;
    session: SocialConnectSession;
    device: ConnectSessionDevice;
  }>(`/v1/creators/${encodeURIComponent(params.creatorId)}/accounts/connect`, {
    platform: params.platform,
  });
  return data;
}

export async function getConnectSession(
  sessionId: string,
): Promise<{ success: boolean; session: SocialConnectSession }> {
  const { data } = await http.get<{
    success: boolean;
    session: SocialConnectSession;
  }>(`/v1/accounts/connect-sessions/${encodeURIComponent(sessionId)}`);
  return data;
}

export async function cancelConnectSession(
  sessionId: string,
): Promise<{ success: boolean; session: SocialConnectSession }> {
  const { data } = await http.post<{
    success: boolean;
    session: SocialConnectSession;
  }>(`/v1/accounts/connect-sessions/${encodeURIComponent(sessionId)}/cancel`);
  return data;
}

export async function reconnectSocialAccount(
  accountId: string,
): Promise<{
  success: boolean;
  session: SocialConnectSession;
  device: ConnectSessionDevice;
}> {
  const { data } = await http.post<{
    success: boolean;
    session: SocialConnectSession;
    device: ConnectSessionDevice;
  }>(`/v1/accounts/${encodeURIComponent(accountId)}/reconnect`);
  return data;
}

export async function disconnectSocialAccount(
  accountId: string,
  opts: { clearDevice?: boolean } = {},
): Promise<{ success: boolean; removed: boolean; deviceCleared: boolean }> {
  const { data } = await http.post<{
    success: boolean;
    removed: boolean;
    deviceCleared: boolean;
  }>(`/v1/accounts/${encodeURIComponent(accountId)}/disconnect`, opts);
  return data;
}

export async function getSocialAccountStatus(
  accountId: string,
): Promise<{ success: boolean; account: SocialAccount }> {
  const { data } = await http.get<{ success: boolean; account: SocialAccount }>(
    `/v1/accounts/${encodeURIComponent(accountId)}/status`,
  );
  return data;
}

export async function checkSocialAccountHealth(
  accountId: string,
): Promise<{ success: boolean; account: SocialAccount }> {
  const { data } = await http.post<{ success: boolean; account: SocialAccount }>(
    `/v1/accounts/${encodeURIComponent(accountId)}/health`,
  );
  return data;
}

export async function listSocialAuditEvents(params?: {
  creatorId?: string;
  accountId?: string;
  event?: SocialAuditEventType;
  limit?: number;
  offset?: number;
}): Promise<{
  success: boolean;
  events: SocialAuditEvent[];
  count: number;
}> {
  const { data } = await http.get<{
    success: boolean;
    events: SocialAuditEvent[];
    count: number;
  }>("/v1/social/audit", { params });
  return data;
}

// =============================================================================
// Creator Authorization
// =============================================================================

export interface CreatorAuthorizationRecord {
  id: string;
  org_id: string;
  creator_user_id: string;
  creator_profile_id: string;
  status: string;
  requested_at: string;
  requested_by: string;
  approved_at: string | null;
  approved_by: string | null;
  revoked_at: string | null;
  revoked_by: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export async function requestCreatorAuthorization(params: {
  creatorId: string;
  notes?: string;
}): Promise<{ success: boolean; authorization: CreatorAuthorizationRecord }> {
  const { data } = await http.post<{ success: boolean; authorization: CreatorAuthorizationRecord }>(
    `/v1/creators/${encodeURIComponent(params.creatorId)}/authorization`,
    { notes: params.notes },
  );
  return data;
}

export async function listCreatorAuthorizations(
  creatorId: string,
): Promise<{ success: boolean; authorizations: CreatorAuthorizationRecord[] }> {
  const { data } = await http.get<{ success: boolean; authorizations: CreatorAuthorizationRecord[] }>(
    `/v1/creators/${encodeURIComponent(creatorId)}/authorization`,
  );
  return data;
}

export async function listMyAuthorizationRequests(): Promise<{
  success: boolean;
  authorizations: CreatorAuthorizationRecord[];
}> {
  const { data } = await http.get<{ success: boolean; authorizations: CreatorAuthorizationRecord[] }>(
    '/v1/creator/authorization-requests',
  );
  return data;
}

export async function approveCreatorAuthorization(
  id: string,
): Promise<{ success: boolean; authorization: CreatorAuthorizationRecord }> {
  const { data } = await http.post<{ success: boolean; authorization: CreatorAuthorizationRecord }>(
    `/v1/creator/authorization/${encodeURIComponent(id)}/approve`,
  );
  return data;
}

export async function rejectCreatorAuthorization(
  id: string,
): Promise<{ success: boolean; authorization: CreatorAuthorizationRecord }> {
  const { data } = await http.post<{ success: boolean; authorization: CreatorAuthorizationRecord }>(
    `/v1/creator/authorization/${encodeURIComponent(id)}/reject`,
  );
  return data;
}

export async function revokeCreatorAuthorization(
  id: string,
): Promise<{ success: boolean; authorization: CreatorAuthorizationRecord }> {
  const { data } = await http.post<{ success: boolean; authorization: CreatorAuthorizationRecord }>(
    `/v1/creator/authorization/${encodeURIComponent(id)}/revoke`,
  );
  return data;
}

export async function getCreatorAuthorizationStatus(
  creatorId: string,
): Promise<{ success: boolean; authorization: CreatorAuthorizationRecord | null }> {
  const { data } = await http.get<{ success: boolean; authorization: CreatorAuthorizationRecord | null }>(
    '/v1/creator/authorization-status',
    { params: { creatorId } },
  );
  return data;
}

// =============================================================================
// Content Approval
// =============================================================================

export interface ContentApprovalRecord {
  id: string;
  org_id: string;
  asset_id: string;
  creator_id: string;
  creator_user_id: string | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  submitted_by: string;
  submitted_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  notes: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
}

export async function requestContentApproval(
  assetId: string,
  notes?: string,
): Promise<{ success: boolean; approval: ContentApprovalRecord }> {
  const { data } = await http.post<{ success: boolean; approval: ContentApprovalRecord }>(
    `/v1/media/assets/${assetId}/approval`,
    { notes },
  );
  return data;
}

export async function getContentApprovalStatus(
  assetId: string,
): Promise<{ success: boolean; approval: ContentApprovalRecord | null }> {
  const { data } = await http.get<{ success: boolean; approval: ContentApprovalRecord | null }>(
    `/v1/media/assets/${assetId}/approval`,
  );
  return data;
}

export async function listMyContentApprovals(): Promise<{
  success: boolean;
  approvals: ContentApprovalRecord[];
}> {
  const { data } = await http.get<{ success: boolean; approvals: ContentApprovalRecord[] }>(
    '/v1/creator/content-approvals',
  );
  return data;
}

export async function approveContent(
  approvalId: string,
): Promise<{ success: boolean; approval: ContentApprovalRecord }> {
  const { data } = await http.post<{ success: boolean; approval: ContentApprovalRecord }>(
    `/v1/creator/content/${approvalId}/approve`,
  );
  return data;
}

export async function rejectContent(
  approvalId: string,
  reason?: string,
): Promise<{ success: boolean; approval: ContentApprovalRecord }> {
  const { data } = await http.post<{ success: boolean; approval: ContentApprovalRecord }>(
    `/v1/creator/content/${approvalId}/reject`,
    { reason },
  );
  return data;
}

export async function revokeContentApproval(
  assetId: string,
): Promise<{ success: boolean; approval: ContentApprovalRecord }> {
  const { data } = await http.post<{ success: boolean; approval: ContentApprovalRecord }>(
    `/v1/media/assets/${assetId}/approval/revoke`,
  );
  return data;
}

export async function listContentApprovals(filters?: {
  creatorId?: string;
  status?: string;
}): Promise<{ success: boolean; approvals: ContentApprovalRecord[] }> {
  const { data } = await http.get<{ success: boolean; approvals: ContentApprovalRecord[] }>(
    '/v1/content/approvals',
    { params: filters },
  );
  return data;
}

// =============================================================================
// Scheduled Posts
// =============================================================================

export interface ScheduledPostRecord {
  id: string;
  orgId: string;
  /**
   * null on a finished post whose creator / media was deleted afterwards: the
   * post is kept as a record (metadata.deletedCreatorId / deletedAssetId).
   */
  creatorId: string | null;
  assetId: string | null;
  socialAccountId: string | null;
  deviceSerial: string | null;
  status: string;
  retryCount: number;
  maxRetries: number;
  errorMessage: string | null;
  scheduledAt: string | null;
  timezone: string | null;
  scheduledBy: string | null;
  failureType: string | null;
  blockedReason: string | null;
  createdAt: string;
  updatedAt: string;
  /** When it went out (or stopped for good). */
  completedAt?: string | null;
  metadata?: Record<string, unknown>;
  /** The media; null when it was deleted after the post finished. */
  asset?: { filename: string; mimeType: string; deleted: boolean } | null;
  account?: { username: string | null; platform: string } | null;
  creatorName?: string | null;
  /** Where a post that went out can be seen: the post itself, or for now the account's profile. */
  publicUrl?: string | null;
  publicUrlKind?: 'post' | 'profile' | null;
}

/** Scheduled Posts tabs. */
export type PostListView = 'upcoming' | 'published' | 'problems' | 'all';

export async function schedulePost(params: {
  contentId: string;
  socialAccountId: string;
  scheduledAt: string;
  timezone: string;
  caption?: string;
  hashtags?: string[];
}): Promise<{ success: boolean; job: ScheduledPostRecord; warnings: string[] }> {
  const { data } = await http.post<{ success: boolean; job: ScheduledPostRecord; warnings: string[] }>(
    '/v1/posts/schedule',
    params,
  );
  return data;
}

// Alerts (header bell): the signed-in user's own operational alerts.
export interface AlertItem {
  id: string;
  kind: string;
  title: string;
  description: string | null;
  severity: 'info' | 'warning' | 'error';
  /** In-app path to open, e.g. "/posts". */
  link: string | null;
  read: boolean;
  createdAt: string;
}

export async function listAlerts(limit = 20): Promise<{ success: boolean; alerts: AlertItem[]; unread: number }> {
  const { data } = await http.get<{ success: boolean; alerts: AlertItem[]; unread: number }>(
    '/v1/notifications',
    { params: { limit } },
  );
  return data;
}

/** Mark the given alerts read, or all of them when `ids` is omitted. */
export async function markAlertsRead(ids?: string[]): Promise<{ success: boolean; updated: number }> {
  const { data } = await http.post<{ success: boolean; updated: number }>(
    '/v1/notifications/read',
    ids === undefined ? {} : { ids },
  );
  return data;
}

/**
 * Edit a post that has not gone out yet. Send only what changes; an empty
 * caption or hashtag list removes it. 409 once the post is going out.
 */
export async function updateScheduledPost(
  postId: string,
  changes: { caption?: string; hashtags?: string[]; scheduledAt?: string; timezone?: string; contentId?: string },
): Promise<{ success: boolean; job: ScheduledPostRecord }> {
  const { data } = await http.patch<{ success: boolean; job: ScheduledPostRecord }>(
    `/v1/posts/${encodeURIComponent(postId)}`,
    changes,
  );
  return data;
}

export async function listScheduledPosts(filters?: {
  creatorId?: string;
  status?: string;
  view?: PostListView;
  /** Only posts that use this media asset. */
  assetId?: string;
}): Promise<{ success: boolean; jobs: ScheduledPostRecord[]; total: number }> {
  const { data } = await http.get<{ success: boolean; jobs: ScheduledPostRecord[]; total: number }>(
    '/v1/posts',
    { params: filters },
  );
  return data;
}

export async function getScheduledPost(
  postId: string,
): Promise<{ success: boolean; job: ScheduledPostRecord }> {
  const { data } = await http.get<{ success: boolean; job: ScheduledPostRecord }>(
    `/v1/posts/${postId}`,
  );
  return data;
}

export async function cancelScheduledPost(
  postId: string,
): Promise<{ success: boolean; job: ScheduledPostRecord }> {
  const { data } = await http.post<{ success: boolean; job: ScheduledPostRecord }>(
    `/v1/posts/${postId}/cancel`,
  );
  return data;
}

export async function retryScheduledPost(
  postId: string,
): Promise<{ success: boolean; message: string }> {
  const { data } = await http.post<{ success: boolean; message: string }>(
    `/v1/publishing/jobs/${postId}/retry`,
  );
  return data;
}

// =============================================================================
// Agency Authorization Requests
// =============================================================================

export interface AgencyAuthorizationRequestRecord {
  id: string;
  requesting_org_id: string;
  target_creator_id: string;
  target_creator_org_id: string;
  status: 'pending' | 'approved' | 'rejected' | 'revoked' | 'expired';
  access_role: string;
  requested_by: string;
  approved_by: string | null;
  requested_at: string;
  resolved_at: string | null;
  expires_at: string | null;
  notes: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export async function createAgencyRequest(params: {
  targetCreatorId: string;
  accessRole: string;
  notes?: string;
  expiresAt?: string;
}): Promise<{ success: boolean; request: AgencyAuthorizationRequestRecord }> {
  const { data } = await http.post<{ success: boolean; request: AgencyAuthorizationRequestRecord }>(
    '/v1/agency-requests',
    params,
  );
  return data;
}

export async function listAgencyRequests(filters?: {
  creator_id?: string;
  status?: string;
}): Promise<{ success: boolean; requests: AgencyAuthorizationRequestRecord[] }> {
  const { data } = await http.get<{ success: boolean; requests: AgencyAuthorizationRequestRecord[] }>(
    '/v1/agency-requests',
    { params: filters },
  );
  return data;
}

export async function approveAgencyRequest(
  requestId: string,
): Promise<{ success: boolean; request: AgencyAuthorizationRequestRecord }> {
  const { data } = await http.post<{ success: boolean; request: AgencyAuthorizationRequestRecord }>(
    `/v1/agency-requests/${requestId}/approve`,
  );
  return data;
}

export async function rejectAgencyRequest(
  requestId: string,
): Promise<{ success: boolean; request: AgencyAuthorizationRequestRecord }> {
  const { data } = await http.post<{ success: boolean; request: AgencyAuthorizationRequestRecord }>(
    `/v1/agency-requests/${requestId}/reject`,
  );
  return data;
}

export async function revokeAgencyRequest(
  requestId: string,
): Promise<{ success: boolean; request: AgencyAuthorizationRequestRecord }> {
  const { data } = await http.post<{ success: boolean; request: AgencyAuthorizationRequestRecord }>(
    `/v1/agency-requests/${requestId}/revoke`,
  );
  return data;
}

// =============================================================================
// Audit Trail
// =============================================================================

export interface AuditEventRecord {
  id: string;
  org_id: string;
  actor_id: string | null;
  actor_email: string | null;
  actor_role: string | null;
  action: string;
  resource_type: string;
  resource_id: string | null;
  creator_id: string | null;
  result: 'SUCCESS' | 'FAILURE';
  metadata: Record<string, unknown>;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

export async function listAuditEvents(filters?: {
  creator_id?: string;
  entity_type?: string;
  actor_id?: string;
  event_type?: string;
  limit?: number;
  offset?: number;
}): Promise<{ success: boolean; events: AuditEventRecord[]; total: number; hasMore: boolean }> {
  const { data } = await http.get<{ success: boolean; events: AuditEventRecord[]; total: number; hasMore: boolean }>(
    '/v1/audit/events',
    { params: filters },
  );
  return data;
}

// =============================================================================
// Creator Invitations (Mode B agency onboarding)
// =============================================================================

export async function createCreatorInvitation(
  params: CreateCreatorInvitationRequest,
): Promise<{ success: boolean; invitation: CreatorInvitationListItem; token: string; url: string; emailSent?: boolean }> {
  const { data } = await http.post<CreateCreatorInvitationResponse & { success: boolean }>(
    '/v1/creator-invitations',
    params,
  );
  return data;
}

export async function listCreatorInvitations(filters?: {
  status?: string;
}): Promise<{ success: boolean; invitations: CreatorInvitationListItem[] }> {
  const { data } = await http.get<{ success: boolean; invitations: CreatorInvitationListItem[] }>(
    '/v1/creator-invitations',
    { params: filters },
  );
  return data;
}

export async function getCreatorInvitation(
  id: string,
): Promise<{ success: boolean; invitation: CreatorInvitationListItem }> {
  const { data } = await http.get<{ success: boolean; invitation: CreatorInvitationListItem }>(
    `/v1/creator-invitations/${encodeURIComponent(id)}`,
  );
  return data;
}

export async function revokeCreatorInvitation(
  id: string,
): Promise<{ success: boolean; invitation: CreatorInvitationListItem }> {
  const { data } = await http.post<{ success: boolean; invitation: CreatorInvitationListItem }>(
    `/v1/creator-invitations/${encodeURIComponent(id)}/revoke`,
  );
  return data;
}

export async function regenerateCreatorInvitation(
  id: string,
): Promise<{ success: boolean; invitation: CreatorInvitationListItem; token: string; url: string; emailSent?: boolean }> {
  const { data } = await http.post<CreateCreatorInvitationResponse & { success: boolean }>(
    `/v1/creator-invitations/${encodeURIComponent(id)}/regenerate`,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Workspace Danger Zone (agency owner only; the server enforces it)
// ---------------------------------------------------------------------------

export interface WorkspaceResetSummary {
  publishingJobs: number;
  calendarEntries: number;
  contentAssignments: number;
  postingHistory: number;
  publishingHistory: number;
  activityLogs: number;
  analyticsMetrics: number;
  connectSessions: number;
  socialAccounts: number;
  legacyAccounts: number;
  legacyAccountsKept: number;
}

export async function resetWorkspaceData(body: {
  confirmName: string;
  password: string;
}): Promise<{ success: boolean; removed: WorkspaceResetSummary }> {
  const { data } = await http.post<{ success: boolean; removed: WorkspaceResetSummary }>(
    '/v1/workspace/reset',
    body,
  );
  return data;
}

export async function deleteAgency(body: {
  confirmName: string;
  password: string;
}): Promise<{ success: boolean; removed: Record<string, number>; devicesReleased: number }> {
  const { data } = await http.post<{ success: boolean; removed: Record<string, number>; devicesReleased: number }>(
    '/v1/workspace/delete',
    body,
  );
  return data;
}
